interface Props {
  visible: boolean;
  onClose: () => void;
}

interface SubprocessorRow {
  name: string;
  purpose: string;
  dataTransmitted: string;
  location: string;
  transferMechanism: string;
  dpaLink?: string;
}

const SUBPROCESSORS: SubprocessorRow[] = [
  {
    name: 'IONOS',
    purpose: "Hébergement de l'application web (frontend et serveur applicatif) accessible à l'adresse app.loievin.fr.",
    dataTransmitted:
      "Ensemble du trafic HTTP/HTTPS de l'application, incluant les requêtes d'analyse, les uploads de fichiers et les réponses du serveur, ainsi que la base de données de l'application (PocketBase : comptes, profils, historique d'analyses, événements d'usage, photos de profil), hébergée sur ce même serveur, sur disque chiffré.",
    location: 'Union européenne — IONOS SARL, 7 place de la Gare, 57200 Sarreguemines, France',
    transferMechanism: 'Aucun transfert hors UE',
    dpaLink: 'https://www.ionos.fr/terms-gtc/terms-privacy',
  },
  {
    name: 'Mistral AI',
    purpose: "Fourniture des modèles d'IA utilisés pour l'analyse (vision, raisonnement, embeddings). Fournisseur d'IA imposé en production pour la souveraineté des données (un garde-fou applicatif empêche le serveur de démarrer avec un autre fournisseur).",
    dataTransmitted:
      "Contenus soumis par l'Utilisateur (textes et images, ces dernières encodées en base64 le temps du traitement) transmis ponctuellement via l'API pour le temps nécessaire au traitement de la requête, ainsi que les requêtes de recherche vectorisées (embeddings). Conformément au contrat de traitement (DPA) de Mistral AI, ces contenus ne sont pas utilisés pour l'entraînement des modèles.",
    location: 'France — Union européenne',
    transferMechanism: 'Aucun transfert hors UE',
    dpaLink: 'https://mistral.ai/terms',
  },
  {
    name: 'Stripe',
    purpose: 'Traitement des paiements et gestion des abonnements.',
    dataTransmitted: 'Email, identifiant client, montant des transactions, moyen de paiement (géré directement par Stripe, ne transite pas par nos serveurs).',
    location: 'États-Unis — Irlande (filiale européenne)',
    transferMechanism: 'Clauses Contractuelles Types (CCT) de la Commission européenne + adhésion au Data Privacy Framework UE–États-Unis',
    dpaLink: 'https://stripe.com/legal/dpa',
  },
  {
    name: 'Légifrance (DILA / PISTE)',
    purpose: 'Récupération des textes de loi et jurisprudences officielles en temps réel.',
    dataTransmitted: "Aucune donnée personnelle n'est transmise. Seules des requêtes anonymes contenant des mots-clés juridiques sont envoyées.",
    location: 'France — service public',
    transferMechanism: 'Aucun transfert hors UE',
    dpaLink: 'https://www.legifrance.gouv.fr',
  },
  {
    name: 'Brevo (ex-Sendinblue)',
    purpose: "Routage des emails transactionnels d'authentification (confirmation d'inscription, réinitialisation de mot de passe, changement d'email) via SMTP, et gestion de la liste d'attente de la page de présentation publique.",
    dataTransmitted: "Pour les emails transactionnels : l'adresse email du destinataire et le contenu du message (lien d'action sécurisé), le temps de l'acheminement. Pour la liste d'attente : l'adresse email (saisie volontaire sur la page de présentation) accompagnée d'un segment d'audience déclaré par l'inscrit (type de profil : marque, agence, juriste, caviste, influenceur, autre) et d'un indicateur de provenance. La conservation et l'effacement de ces données sont gérés au sein de Brevo.",
    location: 'France — Union européenne',
    transferMechanism: 'Aucun transfert hors UE',
    dpaLink: 'https://www.brevo.com/legal/privacypolicy/',
  },
  {
    name: 'Sentry',
    purpose: "Suivi technique des erreurs applicatives (backend Express et frontend React) afin de diagnostiquer les anomalies, assurer la traçabilité exigée par l'audit CNIL et maintenir la qualité de service. Indispensable à l'exercice effectif du droit d'intervention humaine garanti par l'article 22 du RGPD : sans suivi des erreurs, un dysfonctionnement du pipeline IA ne peut être détecté ni corrigé.",
    dataTransmitted:
      "Messages d'erreur techniques, stack traces, URL de la route concernée, nom de l'environnement (développement ou production). Aucune donnée identifiante n'est transmise : les adresses IP, adresses email, cookies, en-têtes d'authentification et en-têtes de session Stripe sont automatiquement supprimés avant envoi grâce à un filtre `beforeSend` côté serveur et client, de même que les champs sensibles connus du corps de requête (mot de passe, jetons d'accès). Aucune captation de session utilisateur (Session Replay désactivé).",
    location: 'Allemagne — Sentry GmbH, Krausenstraße 9-10, 10117 Berlin. Résidence des données configurée sur l\'infrastructure européenne (endpoint `de.sentry.io`).',
    transferMechanism: 'Aucun transfert hors UE (résidence des données UE activée). La société mère Functional Software, Inc. (Delaware, États-Unis) n\'a pas accès aux données stockées en Europe.',
    dpaLink: 'https://sentry.io/legal/dpa/',
  },
  {
    name: 'Langfuse (Cloud EU)',
    purpose:
      "Observabilité et évaluation de la qualité du pipeline d'IA : chaque étape d'une analyse est tracée (modèle, latence, jetons) afin de diagnostiquer les anomalies, détecter une dérive des modèles et exercer effectivement le droit à une intervention humaine garanti par l'article 22 du RGPD (sans cette traçabilité, une analyse contestée ne pourrait être ré-examinée).",
    dataTransmitted:
      "Pour chaque analyse : le texte soumis (nom de marque, slogan, concept, post social, brief d'activation événementielle, question libre) tel qu'envoyé aux modèles, et les verdicts générés. Le cas échéant, le commentaire de retour laissé via le bouton « Signaler une erreur ». Un identifiant d'utilisateur et un identifiant d'organisation rattachés à la trace. Les images téléversées NE sont jamais transmises (seul le texte qui les accompagne l'est). Les secrets techniques (clés, jetons, en-têtes d'authentification) sont automatiquement masqués avant envoi ; le contenu métier, lui, n'est pas masqué.",
    location: 'Union européenne — Langfuse GmbH, hébergement à Francfort (cloud.langfuse.com). L\'infrastructure états-unienne (us.cloud.langfuse.com) est proscrite par configuration.',
    transferMechanism: 'Aucun transfert hors UE (résidence Francfort)',
    dpaLink: 'https://langfuse.com/security',
  },
  {
    name: 'Plausible Analytics',
    purpose: "Mesure d'audience de la page de présentation publique (app.loievin.fr), respectueuse de la vie privée et sans cookie.",
    dataTransmitted:
      "Statistiques de fréquentation strictement anonymes (pages vues, événements), sans cookie, sans adresse IP et sans empreinte numérique. Active uniquement sur la page de présentation publique — aucune mesure d'audience n'est effectuée dans l'application authentifiée.",
    location: 'Union européenne — Plausible Insights OÜ (Estonie), hébergement UE',
    transferMechanism: 'Aucun transfert hors UE',
    dpaLink: 'https://plausible.io/data-policy',
  },
  {
    name: 'Google (connexion via Google)',
    purpose: "Fournisseur d'identité optionnel : l'Utilisateur peut choisir de se connecter avec son compte Google plutôt qu'avec un mot de passe.",
    dataTransmitted:
      "Uniquement si l'Utilisateur choisit la connexion Google : adresse email, nom et photo de profil transmis par Google au moment de la connexion. Une connexion par email et mot de passe, sans Google, reste toujours disponible.",
    location: 'États-Unis — Google LLC (authentification relayée par notre serveur PocketBase, hébergé en France)',
    transferMechanism: 'Clauses Contractuelles Types + Data Privacy Framework UE–États-Unis ; transfert déclenché uniquement si l\'Utilisateur opte pour la connexion Google',
    dpaLink: 'https://policies.google.com/privacy',
  },
];

// Subprocessors list — required by GDPR Art. 28 transparency obligations.
// Update this file whenever a new subprocessor is added or removed.

export default function Subprocessors({ visible, onClose }: Props) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 shader:bg-black/40 shader:backdrop-blur-2xl shader:border shader:border-white/10 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="font-display text-lg text-slate-900 dark:text-white">Sous-traitants</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Liste des sous-traitants techniques utilisés par Evinly (RGPD art.&nbsp;28)</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">

          <p>
            Conformément à l'article&nbsp;28 du RGPD et dans un souci de transparence, nous publions ci-dessous la liste exhaustive des sous-traitants techniques auxquels nous faisons appel pour le fonctionnement du service Evinly. Chacun de ces sous-traitants est encadré par un accord de traitement des données (DPA) conforme aux exigences du RGPD.
          </p>

          <p>
            Toute modification de cette liste (ajout ou remplacement d'un sous-traitant) sera notifiée aux utilisateurs par email ou par une notification au sein du service, avec un préavis permettant d'exercer un éventuel droit d'opposition.
          </p>

          <div className="space-y-4 pt-2">
            {SUBPROCESSORS.map(sp => (
              <div
                key={sp.name}
                className="border border-slate-200 dark:border-slate-700 shader:border-white/10 rounded-lg p-4 space-y-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-base text-slate-900 dark:text-white">{sp.name}</h3>
                  {sp.dpaLink && (
                    <a
                      href={sp.dpaLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-violet-600 dark:text-violet-400 underline shrink-0"
                    >
                      Mentions légales
                    </a>
                  )}
                </div>
                <div className="text-xs space-y-1.5">
                  <div><span className="font-medium text-slate-700 dark:text-slate-300">Finalité&nbsp;:</span> {sp.purpose}</div>
                  <div><span className="font-medium text-slate-700 dark:text-slate-300">Données transmises&nbsp;:</span> {sp.dataTransmitted}</div>
                  <div><span className="font-medium text-slate-700 dark:text-slate-300">Localisation&nbsp;:</span> {sp.location}</div>
                  <div><span className="font-medium text-slate-700 dark:text-slate-300">Encadrement des transferts&nbsp;:</span> {sp.transferMechanism}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10 rounded-lg p-4 mt-4">
            <p className="text-xs text-violet-700 dark:text-violet-300 shader:text-violet-200 leading-relaxed">
              <strong>Principe de sélection&nbsp;:</strong> Evinly privilégie systématiquement des sous-traitants européens pour toutes les opérations touchant à des données personnelles ou à des contenus confidentiels. Les contenus soumis à l'analyse et les visuels sont traités exclusivement par des sous-traitants situés dans l'Union européenne. Les rares recours à des sous-traitants dont la maison-mère est extra-européenne sont strictement encadrés&nbsp;: Stripe (paiement) et, uniquement si l'Utilisateur choisit cette option, Google (connexion via Google) reposent sur des Clauses Contractuelles Types et/ou le Data Privacy Framework UE–États-Unis, et sont limités aux seules données techniquement indispensables. Sentry, bien que filiale d'un groupe américain, stocke les données sur une infrastructure européenne à laquelle sa maison-mère n'a pas accès. En production, un garde-fou applicatif impose Mistral AI (UE) comme unique fournisseur d'IA&nbsp;; un fournisseur alternatif états-unien n'est utilisable qu'en environnement de développement, sur des données de test.
            </p>
          </div>

        </div>

        <div className="p-6 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="font-display w-full py-3 rounded-xl border border-slate-300 dark:border-slate-600 shader:border-white/15 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
